/**
 * Orchestrator Agent - Coordinates All Agents
 * 
 * This agent orchestrates the Location, Hobby, and Tag agents
 * to provide a seamless, intelligent experience.
 */

import LocationAgent from './LocationAgent';
import HobbyAgent from './HobbyAgent';
import TagAgent from './TagAgent';

class OrchestratorAgent {
  constructor() {
    this.locationAgent = new LocationAgent();
    this.hobbyAgent = new HobbyAgent();
    this.tagAgent = new TagAgent();
    this.userProfile = null;
  }

  /**
   * Initialize with user profile
   */
  initialize(userProfile) {
    this.userProfile = userProfile;
    this.locationAgent.setUserProfile(userProfile);
    this.hobbyAgent.setUserProfile(userProfile);
    this.tagAgent.setUserProfile(userProfile);
  }

  /**
   * Intelligent location detection and suggestion
   */
  async getIntelligentLocation(options = {}) {
    const {
      useGPS = true,
      university = null,
      universityLocations = {}
    } = options;

    return await this.locationAgent.getIntelligentLocation({
      useGPS,
      university,
      universityLocations
    });
  }

  /**
   * Intelligent hobby recommendations
   */
  async getIntelligentHobbies(options = {}) {
    return await this.hobbyAgent.getIntelligentHobbies(options);
  }

  /**
   * Intelligent tag suggestions
   */
  getIntelligentTags(options = {}) {
    // Update tag agent with current hobbies
    if (options.hobbies) {
      this.tagAgent.setHobbies(options.hobbies);
    }

    // Update location context
    if (options.location) {
      this.tagAgent.setLocationContext(options.location);
    }

    return this.tagAgent.getIntelligentTags({
      firstTag: options.firstTag,
      secondTag: options.secondTag,
      ...options
    });
  }

  /**
   * Complete intelligent recommendation flow
   * Automatically detects location, suggests hobbies, and recommends tags
   */
  async getCompleteRecommendations(options = {}) {
    const {
      useGPS = true,
      university = null,
      universityLocations = {},
      autoDetect = true
    } = options;

    const result = {
      location: null,
      hobbies: [],
      tags: {
        firstOrder: [],
        secondOrder: [],
        thirdOrder: []
      },
      confidence: 0
    };

    try {
      // 1. Get intelligent location
      if (autoDetect) {
        result.location = await this.getIntelligentLocation({
          useGPS,
          university,
          universityLocations
        });
        
        if (result.location) {
          this.hobbyAgent.setLocationContext(result.location);
          this.tagAgent.setLocationContext(result.location);
        }
      }

      // 2. Get intelligent hobbies
      result.hobbies = await this.getIntelligentHobbies({
        includeProfile: true,
        includeMajors: true,
        includeHistory: true,
        includePopular: !!result.location
      });

      // Update tag agent with hobbies
      this.tagAgent.setHobbies(result.hobbies);

      // 3. Get intelligent tags
      result.tags = this.getIntelligentTags({
        hobbies: result.hobbies
      });

      // Calculate overall confidence
      result.confidence = this.calculateConfidence(result);

    } catch (error) {
      console.error('Error in complete recommendations:', error);
      result.error = error.message;
    }

    return result;
  }

  /**
   * Calculate overall confidence score
   */
  calculateConfidence(result) {
    let confidence = 0;
    let factors = 0;

    if (result.location) {
      confidence += (result.location.confidence || 0.5) * 0.4;
      factors += 0.4;
    }

    if (result.hobbies.length > 0) {
      confidence += 0.3;
      factors += 0.3;
    }

    if (result.tags.firstOrder.length > 0) {
      confidence += 0.3;
      factors += 0.3;
    }

    return factors > 0 ? confidence / factors : 0;
  }

  /**
   * Update context when user makes selections
   */
  updateContext(updates) {
    if (updates.location) {
      this.locationAgent.locationCache = updates.location;
      this.hobbyAgent.setLocationContext(updates.location);
      this.tagAgent.setLocationContext(updates.location);
    }

    if (updates.hobbies) {
      this.hobbyAgent.setUserProfile({
        ...this.userProfile,
        hobbies: updates.hobbies
      });
      this.tagAgent.setHobbies(updates.hobbies);
    }

    if (updates.tags) {
      // Tags updated, can trigger new suggestions
    }
  }

  /**
   * Track user interaction for learning
   */
  trackInteraction(type, data) {
    switch (type) {
      case 'group_view':
        this.hobbyAgent.trackBrowsing(data);
        break;
      case 'hobby_select':
        this.tagAgent.setHobbies(data);
        break;
      case 'location_change':
        this.updateContext({ location: data });
        break;
      default:
        break;
    }
  }
}

export default OrchestratorAgent;

